import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, MoreHorizontal, Edit2, Lock } from 'lucide-react';
import AssociatedTopics from './AssociatedTopics';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';

const TopicHeader = ({
  basket,
  basketDescription,
  associatedTopics,
  onBack,
  onDescriptionUpdate,
  topic,
}) => {
  const { isPremium, isLoading } = usePremium();
  const topicLocked = isLocked(topic, isPremium, isLoading);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(basketDescription || '');
  const descriptionInputRef = useRef(null);

  const handleSaveDescription = () => {
    onDescriptionUpdate(editedDescription);
    setIsEditingDescription(false);
  };

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [isEditingDescription]);

  return (
    <header className="bg-cranberry-700 text-white p-4 shadow-md">
      <div className="max-w-6xl mx-auto flex justify-between items-center">
        <button 
          onClick={onBack} 
          className="rounded-full hover:bg-cranberry-600 p-2 transition-colors duration-300"
          aria-label="Go back"
        >
          <ArrowLeft size={20} className="md:w-6 md:h-6" />
        </button>
        <div className="relative group">
          <h1 className="text-lg md:text-xl font-bold truncate mx-4">
            {basket}
          </h1>
          {basket && (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4 z-20 min-w-[200px] max-w-[calc(100vw-2rem)] sm:min-w-[260px] sm:max-w-[340px] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <div className="bg-gray-900 text-white px-3 sm:px-4 py-3 sm:py-3.5 rounded-lg shadow-lg relative">
                {isEditingDescription ? (
                  <div>
                    <textarea
                      ref={descriptionInputRef}
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      className="w-full p-2 bg-neutral-800 rounded border border-neutral-700 text-white text-xs"
                      placeholder="Add a description..."
                      rows={3}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveDescription();
                        }
                      }}
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        onClick={() => {
                          setIsEditingDescription(false);
                          setEditedDescription(basketDescription || '');
                        }}
                        className="px-3 py-1.5 text-xs rounded hover:bg-neutral-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveDescription}
                        className="px-3 py-1.5 text-xs bg-cranberry-600 rounded hover:bg-cranberry-500"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-2 sm:mb-2.5">
                      Why it's here
                    </p>
                    {topicLocked ? (
                      <p className="leading-relaxed text-sm text-gray-300 inline-flex items-center gap-1.5">
                        <Lock size={12} aria-hidden="true" />
                        Connection details are Premium
                      </p>
                    ) : (
                      <>
                        <p className="leading-relaxed text-sm whitespace-pre-wrap">
                          {basketDescription || 'No description'}
                        </p>
                        <button
                          onClick={() => setIsEditingDescription(true)}
                          className="absolute top-0 right-0 p-1.5 rounded-full hover:bg-neutral-700"
                          aria-label="Edit description"
                        >
                          <Edit2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {/* Arrow pointer */}
              <div className="w-2 h-2 bg-gray-900 rotate-45 absolute left-1/2 transform -translate-x-1/2 -top-1"></div>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2 md:space-x-4">
          <AssociatedTopics topics={associatedTopics} />
          <button 
            className="rounded-full hover:bg-cranberry-600 p-2 transition-colors duration-300"
            onClick={() => document.getElementById('topicMenu').classList.toggle('hidden')}
            aria-label="More options"
          >
            <MoreHorizontal size={20} className="md:w-6 md:h-6" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default TopicHeader;
